"use client";

import { useNextRouterContext } from "@faire/retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { useHeaderDesktopContent } from "@faire/retailer-visitor-shared/components/Header/desktop/useHeaderDesktopContent";
import {
  AccessibilityAnchor,
  AccessibilityLink,
} from "@faire/retailer-visitor-shared/lib/AccessibilityLink";
import { home } from "@faire/retailer-visitor-shared/paths/index";
import {
  useAssignSettingLoggedInHomePageRouteChangePrefetching,
  useSettingLoggedInHomePageRouteChangePrefetching,
} from "@faire/retailer-visitor-shared/settings/useSettingLoggedInHomePageRouteChangePrefetching";
import { useStrictLocalization } from "@faire/web/common/localization";
import { faireBaseUrl } from "@faire/web/common/routes/faireBaseUrl";
import { getSpacing } from "@faire/web/slate/spacing";
import { FaireLogo } from "@faire/web/ui/logos/FaireLogo";
import { PrefetchKind } from "next/dist/client/components/router-reducer/router-reducer-types";
import React, { useMemo } from "react";
import styled, { css } from "styled-components";

export const HeaderDesktopLogo = ({
  omitStyling,
}: {
  omitStyling?: boolean;
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { isFaireDirectActiveInLoggedOut } = useHeaderDesktopContent();
  const { prefetch: nextPrefetch } = useNextRouterContext();

  const accessibleLogoLabel = strictLocalize({
    id: "CA6lm2",
    defaultMessage: "Go to Faire homepage",
    description: {
      text: "Description describing to screen reader users what happens when they click the Faire logo.",
    },
  });

  const isLoggedInHomePageRouteChangePrefetching =
    useSettingLoggedInHomePageRouteChangePrefetching();
  const assignSettingLoggedInHomePageRouteChangePrefetching =
    useAssignSettingLoggedInHomePageRouteChangePrefetching();

  const onLogoHover = useMemo(() => {
    return () => {
      nextPrefetch(isFaireDirectActiveInLoggedOut ? faireBaseUrl() : home(), {
        kind: isLoggedInHomePageRouteChangePrefetching
          ? PrefetchKind.FULL
          : PrefetchKind.AUTO,
      });
    };
  }, [
    nextPrefetch,
    isFaireDirectActiveInLoggedOut,
    isLoggedInHomePageRouteChangePrefetching,
  ]);

  const onLogoClick = useMemo(() => {
    return () => assignSettingLoggedInHomePageRouteChangePrefetching();
  }, [assignSettingLoggedInHomePageRouteChangePrefetching]);

  const sharedProps = {
    id: "faire-logo-link",
    "aria-label": accessibleLogoLabel,
    onMouseEnter: onLogoHover,
    onTouchStart: onLogoHover,
    onClick: onLogoClick,
  };

  if (isFaireDirectActiveInLoggedOut) {
    return (
      <LogoAnchor href={faireBaseUrl()} {...sharedProps}>
        <FaireLogo />
      </LogoAnchor>
    );
  }

  if (omitStyling) {
    return (
      <AccessibilityLink to={home()} {...sharedProps}>
        <FaireLogo />
      </AccessibilityLink>
    );
  }

  return (
    <LogoLink to={home()} {...sharedProps}>
      <FaireLogo />
    </LogoLink>
  );
};

const LogoStyling = css`
  padding: ${getSpacing("1x", "2x")};
  margin: 0 ${getSpacing("1x")};
`;

const LogoAnchor = styled(AccessibilityAnchor)`
  ${LogoStyling}
`;

const LogoLink = styled(AccessibilityLink)`
  ${LogoStyling}
`;
